import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Lock, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError(t('auth.passwordMinLength'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('auth.passwordsNoMatch'));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      if (res.ok) {
        setSuccess(true);
      } else {
        const data = await res.json();
        setError(data.detail || 'Something went wrong');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 pt-20">
          <div className="w-full max-w-md text-center">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-[#0d1442] mb-2">Invalid Reset Link</h1>
              <p className="text-slate-500 mb-6">This password reset link is invalid or has expired.</p>
              <Link to="/forgot-password" className="text-[#1a237e] font-medium hover:underline">
                {t('auth.forgotPassword')}
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 pt-20">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1a237e] rounded-full mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 data-testid="reset-password-title" className="text-2xl font-bold text-[#0d1442]">{t('auth.resetPassword')}</h1>
            </div>

            {success ? (
              <div data-testid="reset-password-success" className="text-center">
                <div className="flex items-center justify-center gap-2 p-4 mb-6 bg-green-50 border border-green-200 rounded-lg text-green-700">
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{t('auth.resetSuccess')}</span>
                </div>
                <Link
                  to="/login"
                  className="inline-block py-3 px-6 bg-[#1a237e] text-white font-medium rounded-lg hover:bg-[#0d1442] transition-colors"
                >
                  {t('auth.signIn')}
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="reset-password-form">
                {error && (
                  <div data-testid="reset-password-error" className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div className="mb-4">
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">{t('auth.newPassword')}</label>
                  <div className="relative">
                    <input
                      data-testid="reset-password-input"
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a237e] focus:border-transparent pr-12"
                      required
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-2">{t('auth.confirmPassword')}</label>
                  <input
                    data-testid="reset-confirm-password-input"
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a237e] focus:border-transparent"
                    required
                  />
                </div>

                <button
                  data-testid="reset-password-submit-button"
                  type="submit" disabled={loading}
                  className="w-full py-3 bg-[#1a237e] text-white font-medium rounded-lg hover:bg-[#0d1442] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? '...' : t('auth.resetButton')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPasswordPage;
